import Papa from 'papaparse';

export interface ParsedUserRow {
  email: string;
  firstName?: string;
  lastName?: string;
  error?: string;
}

export interface ParseUsersCsvResult {
  rows: ParsedUserRow[];
  error?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parse a CSV string into a list of user rows for bulk creation.
 * Pure of any browser/file APIs so it can be unit-tested.
 *
 * Headers are case-insensitive; firstName/first_name and lastName/last_name
 * aliases both work. Emails are lowercased + trimmed; rows are flagged with
 * a per-row error for missing/invalid/duplicate emails so the UI can render
 * a preview without rejecting the whole file.
 */
export function parseUsersCsv(csvText: string): ParseUsersCsvResult {
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    delimiter: ',',
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase().replace(/^\ufeff/, '')
  });

  if (result.errors.length > 0) {
    return { rows: [], error: `CSV parse error: ${result.errors[0]?.message ?? 'unknown'}` };
  }

  const rows: ParsedUserRow[] = [];
  const seen = new Set<string>();

  for (const raw of result.data) {
    const email = (raw['email'] || '').trim().toLowerCase();
    const firstName = (raw['firstname'] || raw['first_name'] || '').trim();
    const lastName = (raw['lastname'] || raw['last_name'] || '').trim();

    let error: string | undefined;
    if (!email) error = 'Missing email';
    else if (!EMAIL_RE.test(email)) error = 'Invalid email';
    else if (seen.has(email)) error = 'Duplicate in file';
    if (!error) seen.add(email);

    rows.push({
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      error
    });
  }

  if (rows.length === 0) {
    return { rows: [], error: 'CSV contains no rows' };
  }

  return { rows };
}
